import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { domainForHunt } from '../../lib/huntDomain';
import { importFromNumberHunter } from '../../lib/importNumberHunter';
import styles from './Header.module.css';

const SPARK_BUCKETS = 14;

// Buckets the hunt's domain by index position (not numeric value) so this
// works the same for Numbers, Alphabet, or any other fixed-domain hunt type.
function Sparkline({ domain, capturedItems }) {
  const bucketSize = domain.length / SPARK_BUCKETS;
  const bars = Array.from({ length: SPARK_BUCKETS }, (_, i) => {
    const start = Math.floor(i * bucketSize);
    const end = Math.floor((i + 1) * bucketSize);
    let captured = 0;
    let total = 0;
    for (let idx = start; idx < end; idx++) {
      total++;
      if (capturedItems.has(domain[idx])) captured++;
    }
    return total > 0 ? captured / total : 0;
  });

  return (
    <div className={styles.sparkline} aria-hidden="true">
      {bars.map((fraction, i) => (
        <div key={i} className={styles.sparkBar}>
          <div className={styles.sparkBarGap} />
          <div className={styles.sparkBarFill} style={{ height: `${Math.max(fraction * 100, fraction > 0 ? 8 : 0)}%` }} />
        </div>
      ))}
    </div>
  );
}

export default function Header({ onExport, onImport, theme, toggleTheme }) {
  const { state, dispatch, derived, activeHunt, user, signInWithGoogle, signOut, upsertPhotoState, upsertHuntState } = useAppContext();
  const { canInstall, triggerInstall } = useInstallPrompt();
  const { stats, capturedItems } = derived;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [importingNH, setImportingNH] = useState(false);
  const [huntMenuOpen, setHuntMenuOpen] = useState(false);
  const huntMenuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!huntMenuOpen) return;
    function handlePointerDown(e) {
      if (huntMenuRef.current && !huntMenuRef.current.contains(e.target)) setHuntMenuOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setHuntMenuOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [huntMenuOpen]);

  function setView(view) {
    dispatch({ type: 'SET_VIEW', view });
  }

  function selectHunt(huntId) {
    setHuntMenuOpen(false);
    dispatch({ type: 'SET_ACTIVE_HUNT', huntId });
  }

  async function handleImportNumberHunter() {
    if (!user || importingNH) return;
    if (!window.confirm('Import your existing Number Hunter photos into a Hunt here? This only adds new photos here — nothing in Number Hunter is changed.')) {
      return;
    }
    setMenuOpen(false);
    setImportingNH(true);
    try {
      const result = await importFromNumberHunter({ user, state, upsertPhotoState, upsertHuntState });
      if (result.imported === 0) {
        window.alert('No Number Hunter photos found to import.');
      } else {
        window.alert(`Imported ${result.imported} photo${result.imported !== 1 ? 's' : ''} into "${result.hunt.title}".`);
        dispatch({ type: 'SET_ACTIVE_HUNT', huntId: result.hunt.id });
      }
    } catch (err) {
      console.error('Import from Number Hunter failed:', err);
      window.alert('Import failed. Check your connection and try again.');
    } finally {
      setImportingNH(false);
    }
  }

  const isCustom = activeHunt?.type === 'custom';

  const navItems = [
    { id: 'grid',      label: 'GRID' },
    ...(isCustom ? [] : [{ id: 'hunt', label: `HUNT ${(activeHunt?.huntList ?? []).length}` }]),
    { id: 'locations', label: `PLACES ${stats.locations}` },
  ];
  if (user) navItems.push({ id: 'share', label: 'SHARE' });

  return (
    <header className={styles.header}>
      <div className={styles.topRow}>
        <button className={styles.wordmark} onClick={() => setView('grid')}>
          Photo Hunter
        </button>

        <div className={styles.menuWrap} ref={menuRef}>
          <button
            className={styles.menuTrigger}
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label="Account menu"
          >
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className={styles.avatar}
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className={styles.avatarFallback}>{user ? (user.displayName?.[0] ?? '·') : '···'}</span>
            )}
          </button>

          {menuOpen && (
            <div className={styles.menu} role="menu">
              {user ? (
                <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); signOut(); }}>
                  Sign Out
                </button>
              ) : (
                <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); signInWithGoogle(); }}>
                  Sign in with Google
                </button>
              )}
              {user && (
                <button className={styles.menuItem} role="menuitem" onClick={handleImportNumberHunter} disabled={importingNH}>
                  {importingNH ? 'Importing…' : 'Import from Number Hunter'}
                </button>
              )}
              <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); onExport(); }}>
                Export
              </button>
              <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); onImport(); }}>
                Import
              </button>
              <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); toggleTheme(); }}>
                {theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
              </button>
              {canInstall && (
                <button className={styles.menuItem} role="menuitem" onClick={() => { setMenuOpen(false); triggerInstall(); }}>
                  Install App
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {activeHunt && (
        <div className={styles.huntSwitcherWrap} ref={huntMenuRef}>
          <button
            className={styles.huntSwitcher}
            onClick={() => setHuntMenuOpen(o => !o)}
            aria-haspopup="true"
            aria-expanded={huntMenuOpen}
          >
            {activeHunt.title} <span className={styles.huntSwitcherCaret}>{huntMenuOpen ? '▴' : '▾'}</span>
          </button>

          {huntMenuOpen && (
            <div className={styles.huntMenu} role="menu">
              {state.hunts.map(hunt => (
                <button
                  key={hunt.id}
                  className={`${styles.huntMenuItem} ${hunt.id === activeHunt.id ? styles.huntMenuItemActive : ''}`}
                  role="menuitem"
                  onClick={() => selectHunt(hunt.id)}
                >
                  {hunt.id === activeHunt.id ? '✓ ' : ''}{hunt.title}
                </button>
              ))}
              <div className={styles.huntMenuDivider} />
              <button
                className={styles.huntMenuItem}
                role="menuitem"
                onClick={() => { setHuntMenuOpen(false); dispatch({ type: 'OPEN_MODAL', modal: 'newHunt' }); }}
              >
                + New Hunt
              </button>
              <button
                className={styles.huntMenuItem}
                role="menuitem"
                onClick={() => { setHuntMenuOpen(false); dispatch({ type: 'OPEN_MODAL', modal: 'manageHunts' }); }}
              >
                Manage Hunts
              </button>
            </div>
          )}
        </div>
      )}

      <div className={styles.statsRow}>
        <div className={styles.displayFigure}>
          <span className={styles.displayNum}>{stats.captured}</span>
          <span className={styles.displaySlash}>/{stats.total}</span>
        </div>
        {!isCustom && activeHunt && (
          <Sparkline
            domain={domainForHunt(activeHunt, [...derived.photosByItem.values()].flat())}
            capturedItems={capturedItems}
          />
        )}
      </div>

      <nav className={styles.nav}>
        {navItems.map(({ id, label }) => (
          <button
            key={id}
            className={`${styles.navBtn} ${state.activeView === id ? styles.active : ''}`}
            onClick={() => id === 'share' ? dispatch({ type: 'OPEN_MODAL', modal: 'share' }) : setView(id)}
          >
            {label}
          </button>
        ))}
      </nav>
    </header>
  );
}
